/**
 * PropertiesPanel (P2.1-S5 polish) — wrap content trong 3-section Accordion.
 *
 * Sections:
 *  1. General — type-specific editor (TextProps/NumberProps/SelectProps/...)
 *  2. Logic & conditions — `LogicRuleEditor`
 *  3. Advanced — placeholder cho binding/lock metadata (đã có inline trong common
 *     editor; section này hiện chỉ show binding info if any)
 */
import * as React from "react";

import type { FieldType, FormFieldSchema } from "@/api/forms";
import { LogicRuleEditor } from "@/components/form-builder/LogicRuleEditor";
import { AsyncSelectProps } from "@/components/form-builder/properties/AsyncSelectProps";
import { CheckboxProps } from "@/components/form-builder/properties/CheckboxProps";
import {
  CommonProps,
  type PropsEditorProps,
} from "@/components/form-builder/properties/common";
import { DateProps } from "@/components/form-builder/properties/DateProps";
import { NumberProps } from "@/components/form-builder/properties/NumberProps";
import { RadioProps } from "@/components/form-builder/properties/RadioProps";
import { RepeaterProps } from "@/components/form-builder/properties/RepeaterProps";
import { SelectProps } from "@/components/form-builder/properties/SelectProps";
import { TextProps } from "@/components/form-builder/properties/TextProps";
import { Accordion, AccordionSection } from "@/components/ui/accordion";
import { getFieldAt, useFormBuilderStore } from "@/stores/formBuilder";

const TYPE_EDITORS: Partial<
  Record<FieldType, React.ComponentType<PropsEditorProps>>
> = {
  text: TextProps,
  textarea: TextProps,
  number: NumberProps,
  date: DateProps,
  datetime: DateProps,
  select: SelectProps,
  multi_select: SelectProps,
  radio: RadioProps,
  async_select: AsyncSelectProps,
  checkbox: CheckboxProps,
  repeater: RepeaterProps,
};

export function PropertiesPanel(): JSX.Element {
  const schema = useFormBuilderStore((s) => s.schema);
  const selectedPath = useFormBuilderStore((s) => s.selectedPath);
  const updateField = useFormBuilderStore((s) => s.updateField);

  const field = getFieldAt(schema, selectedPath);

  if (!field || !schema || !selectedPath) {
    return (
      <aside className="w-80 shrink-0 border-l bg-background overflow-y-auto">
        <div className="px-3 py-2 border-b">
          <h2 className="text-xs font-semibold text-muted-foreground uppercase">
            Properties
          </h2>
        </div>
        <div className="p-6 text-sm text-muted-foreground text-center">
          Chọn một field trong canvas để edit.
        </div>
      </aside>
    );
  }

  const Editor = TYPE_EDITORS[field.type as FieldType] ?? CommonProps;
  const onUpdate = (patch: Partial<FormFieldSchema>): void =>
    updateField(selectedPath, patch);

  const binding = field.binding;
  const lockLevel = (field as { lock_level?: string }).lock_level;
  const hasAdvanced = Boolean(
    binding?.erp_model || binding?.label_field || (lockLevel && lockLevel !== "any"),
  );

  return (
    <aside className="w-80 shrink-0 border-l bg-background overflow-y-auto">
      <div className="px-3 py-2 border-b sticky top-0 bg-background z-10">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase">
          Properties · <code className="text-foreground">{field.name}</code>
        </h2>
      </div>
      <div className="p-3">
        <Accordion>
          <AccordionSection
            id="general"
            title="General"
            subtitle={field.type}
            defaultOpen
          >
            <Editor field={field} onUpdate={onUpdate} />
          </AccordionSection>
          <AccordionSection
            id="logic"
            title="Logic & Conditions"
            subtitle={field.show_if ? "đã có rule" : "chưa cấu hình"}
          >
            <LogicRuleEditor field={field} schema={schema} onUpdate={onUpdate} />
          </AccordionSection>
          <AccordionSection
            id="advanced"
            title="Advanced"
            subtitle={hasAdvanced ? "binding / lock" : "no binding"}
          >
            <AdvancedSection field={field} />
          </AccordionSection>
        </Accordion>
      </div>
    </aside>
  );
}

function AdvancedSection({ field }: { field: FormFieldSchema }): JSX.Element {
  const binding = field.binding;
  const lockLevel = (field as { lock_level?: string }).lock_level;

  return (
    <div className="space-y-2 text-xs">
      {binding?.erp_model ? (
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">ERP model</span>
          <code className="text-[11px]">{binding.erp_model}</code>
        </div>
      ) : null}
      {binding?.label_field ? (
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">Label field</span>
          <code className="text-[11px]">{binding.label_field}</code>
        </div>
      ) : null}
      {binding?.search_fields && binding.search_fields.length > 0 ? (
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">Search fields</span>
          <code className="text-[11px]">{binding.search_fields.join(", ")}</code>
        </div>
      ) : null}
      {lockLevel && lockLevel !== "any" ? (
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">Lock level</span>
          <span className="text-[11px]">{lockLevel}</span>
        </div>
      ) : null}
      {!binding?.erp_model && !binding?.label_field && (!lockLevel || lockLevel === "any") ? (
        <p className="text-muted-foreground italic">
          Chưa có binding/lock. Dùng dialog Override (4-layer) trên canvas
          để cấu hình.
        </p>
      ) : null}
    </div>
  );
}
